import React, { useState, useEffect, useRef } from "react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import VariableInput from "../Variables/VariableInput";
import { useNotification } from "../../../hooks";

const commonHeaders = [
  "Accept",
  "Accept-Encoding",
  "Authorization",
  "Cache-Control",
  "Content-Type",
  "Cookie",
  "Origin",
  "User-Agent",
  "X-Requested-With",
];

const parseHeaderMap = (value) => {
  const parseJsonRecursively = (str) => {
    try {
      const parsed = JSON.parse(str);
      if (typeof parsed === "string") {
        return parseJsonRecursively(parsed);
      }
      return parsed;
    } catch (error) {
      console.error("HeadersEditor - Error parsing JSON:", error);
      return {};
    }
  };

  let parsedHeaders;
  if (typeof value === "string") {
    parsedHeaders = parseJsonRecursively(value);
  } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    parsedHeaders = value;
  } else {
    parsedHeaders = {};
  }

  if (typeof parsedHeaders !== "object" || parsedHeaders === null || Array.isArray(parsedHeaders)) {
    parsedHeaders = {};
  }

  return parsedHeaders;
};

// `onDisabledChange` is optional so this component still works wherever a caller only cares
// about enabled headers (e.g. no persistence for disabled rows is wired up yet).
const HeadersEditor = ({ value, onChange, disabledValue, onDisabledChange }) => {
  const [headers, setHeaders] = useState([{ key: "", value: "", enabled: true }]);
  // Tracks the last `value`/`disabledValue` this component itself emitted, so local edits
  // aren't re-parsed over the rows while a genuinely new value (switching endpoint or test
  // case) still syncs.
  const lastSyncedValue = useRef(null);
  const lastSyncedDisabledValue = useRef(null);
  const notify = useNotification();

  useEffect(() => {
    if (value === lastSyncedValue.current && disabledValue === lastSyncedDisabledValue.current) {
      return;
    }
    lastSyncedValue.current = value;
    lastSyncedDisabledValue.current = disabledValue;

    try {
      const enabledEntries = Object.entries(parseHeaderMap(value)).map(([key, val]) => ({
        key,
        value: val,
        enabled: true,
      }));
      const disabledEntries = Object.entries(parseHeaderMap(disabledValue)).map(([key, val]) => ({
        key,
        value: val,
        enabled: false,
      }));

      const headerArray = [...enabledEntries, ...disabledEntries];

      // Always ensure we have at least one empty header row
      setHeaders(headerArray.length ? headerArray : [{ key: "", value: "", enabled: true }]);
    } catch (error) {
      console.error("Error parsing headers:", error);

      setHeaders([{ key: "", value: "", enabled: true }]);
    }
  }, [value, disabledValue]);

  const updateHeaders = (newHeaders) => {
    setHeaders(newHeaders);

    const toHeaderObject = (rows) =>
      rows.reduce(
        (acc, h) => ({
          ...acc,
          [h.key]: h.value,
        }),
        {},
      );

    const enabledRows = newHeaders.filter((h) => h.enabled && h.key.trim());
    const disabledRows = newHeaders.filter((h) => !h.enabled && h.key.trim());

    const headerObject = toHeaderObject(enabledRows);
    const disabledHeaderObject = toHeaderObject(disabledRows);

    const outgoingValue =
      Object.keys(headerObject).length === 0 ? "{}" : JSON.stringify(headerObject, null, 2);
    const outgoingDisabledValue =
      Object.keys(disabledHeaderObject).length === 0
        ? "{}"
        : JSON.stringify(disabledHeaderObject, null, 2);

    // Mark both as already-synced so the round-tripped props don't get re-parsed and wipe out
    // rows that only exist because they're deliberately excluded from the "enabled" output.
    lastSyncedValue.current = outgoingValue;
    lastSyncedDisabledValue.current = outgoingDisabledValue;
    onChange(outgoingValue);
    if (onDisabledChange) {
      onDisabledChange(outgoingDisabledValue);
    }
  };

  const addHeader = () => {
    updateHeaders([...headers, { key: "", value: "", enabled: true }]);
  };

  const removeHeader = (index) => {
    const newHeaders = headers.filter((_, i) => i !== index);
    updateHeaders(newHeaders.length ? newHeaders : [{ key: "", value: "", enabled: true }]);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    updateHeaders(newHeaders);
  };

  const toggleHeader = (index) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], enabled: !newHeaders[index].enabled };
    updateHeaders(newHeaders);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto p-4">
        <div className="space-y-3">
          {headers.map((header, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 p-3 border rounded transition-colors ${
                header.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={header.enabled}
                onChange={() => toggleHeader(index)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
              />
              <div className="flex-1 flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => updateHeader(index, "key", e.target.value)}
                    placeholder="Header name"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                    list={`header-suggestions-${index}`}
                  />
                  <datalist id={`header-suggestions-${index}`}>
                    {commonHeaders.map((h) => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                </div>
                <div className="flex-1">
                  <VariableInput
                    value={header.value}
                    onChange={(value) => {
                      updateHeader(index, "value", value);
                      if (value.length > 5000) {
                        notify("Value length exceeds 5000 length", "warning");
                      }
                    }}
                    placeholder="Value"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                    showAutocomplete={true}
                  />
                </div>
              </div>
              <button
                onClick={() => removeHeader(index)}
                className={`p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ${
                  headers.length === 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
                disabled={headers.length === 1}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            onClick={addHeader}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded w-full justify-center"
          >
            <PlusIcon className="h-4 w-4" />
            Add Header
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeadersEditor;
